import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, Divider, Paper, Typography } from '@mui/material';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { format, parseISO } from 'date-fns';
import {
  DevelopingFeedItem,
  DevelopingFeedResponse,
  fetchDevelopingFeed,
  fetchTrending24h,
  fetchWorldIntensity24h,
  Trending24hResponse,
  WorldIntensity24hResponse,
} from '../api/worldInsights';
import { AnalyticProfileContext } from '../contexts/AnalyticProfileContext';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

/** Water behind land polygons (react-simple-maps has no separate ocean layer for this topo). */
const OCEAN_FILL = '#1d4ed8';
/** Land with no activity in the window. */
const DEFAULT_COUNTRY_FILL = '#d4d4d8';

function lerpChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

/** Activity 0→1: green (low) → yellow (mid) → red (high). */
function intensityColor(t: number): string {
  const u = Math.max(0, Math.min(1, t));
  const low = { r: 34, g: 197, b: 94 }; // green-500
  const mid = { r: 234, g: 179, b: 8 }; // yellow-500
  const high = { r: 239, g: 68, b: 68 }; // red-500
  if (u <= 0.5) {
    const k = u / 0.5;
    return `rgb(${lerpChannel(low.r, mid.r, k)},${lerpChannel(low.g, mid.g, k)},${lerpChannel(low.b, mid.b, k)})`;
  }
  const k = (u - 0.5) / 0.5;
  return `rgb(${lerpChannel(mid.r, high.r, k)},${lerpChannel(mid.g, high.g, k)},${lerpChannel(mid.b, high.b, k)})`;
}

function geoProps(geo: { properties?: Record<string, unknown> }): Record<string, unknown> {
  return (geo.properties ?? {}) as Record<string, unknown>;
}

/** Match topojson country to ISO alpha-2 (same heuristics as CompanyLocationMap). */
function getGeoIso2(geo: { properties?: Record<string, unknown> }): string {
  const p = geoProps(geo);
  const raw = String(p.ISO_A2 ?? p.iso_a2 ?? p.WB_A2 ?? '').toUpperCase();
  if (raw && raw !== '-99') return raw;
  const adm = String(p.ADM0_A3 ?? '').toUpperCase();
  if (adm.length === 3) {
    const fromAdm: Record<string, string> = {
      USA: 'US',
      GBR: 'GB',
      FRA: 'FR',
      NOR: 'NO',
      FIN: 'FI',
      SWE: 'SE',
      RUS: 'RU',
      CHN: 'CN',
      JPN: 'JP',
      IND: 'IN',
      BRA: 'BR',
      AUS: 'AU',
      CAN: 'CA',
      DEU: 'DE',
      ITA: 'IT',
      ESP: 'ES',
      NLD: 'NL',
      BEL: 'BE',
      POL: 'PL',
      MEX: 'MX',
      NGA: 'NG',
      ZAF: 'ZA',
      KOR: 'KR',
      ARG: 'AR',
    };
    if (fromAdm[adm]) return fromAdm[adm];
  }
  return '';
}

function choroplethFill(iso2: string, intensityByIso: Map<string, number>, max: number): string {
  const v = intensityByIso.get(iso2) ?? 0;
  if (!v || max <= 0) return DEFAULT_COUNTRY_FILL;
  return intensityColor(v / max);
}

function hoverFillForIntensity(v: number, max: number): string {
  if (!v || max <= 0) return '#ececec';
  const t = Math.min(1, v / max + 0.1);
  return intensityColor(t);
}

function sortDevelopingFeed(data: DevelopingFeedResponse): DevelopingFeedItem[] {
  // A cluster with no articles inside the 24h window is idle. It belongs on the
  // Clusters tab, not in this feed. Among the rest, the fullest stories lead.
  return data.items
    .filter((item) => item.articleCount > 0)
    .sort((a, b) => {
      if (b.articleCount !== a.articleCount) return b.articleCount - a.articleCount;
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    });
}

/** Match backend display: underscores → spaces. */
function formatTopicLabel(label: string): string {
  return label.trim().replaceAll('_', ' ');
}

function sortTopicsByMentionsDesc<T extends { mentions: number }>(topics: T[]): T[] {
  return [...topics].sort((a, b) => b.mentions - a.mentions);
}

function sortKeywordsByScoreDesc<T extends { score: number }>(keywords: T[]): T[] {
  return [...keywords].sort((a, b) => b.score - a.score);
}

const panelSx = {
  bgcolor: '#09090b',
  border: '1px solid #27272a',
  borderRadius: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
} as const;

const panelHeaderSx = {
  px: 2,
  py: 1,
  borderBottom: '1px solid #27272a',
  bgcolor: '#18181b',
  flexShrink: 0,
} as const;

export type LayoutWorldInsightsProps = {
  /** When true, used inside Content Radar with less vertical chrome so the map stays inside its panel. */
  embedded?: boolean;
};

const LayoutWorldInsights: React.FC<LayoutWorldInsightsProps> = ({ embedded = false }) => {
  const { selectedProfileId } = useContext(AnalyticProfileContext);
  const [world, setWorld] = useState<WorldIntensity24hResponse | null>(null);
  const [trending, setTrending] = useState<Trending24hResponse | null>(null);
  const [feed, setFeed] = useState<DevelopingFeedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [w, t, f] = await Promise.all([
          fetchWorldIntensity24h(),
          fetchTrending24h(selectedProfileId),
          fetchDevelopingFeed(selectedProfileId),
        ]);
        if (!cancelled) {
          setWorld(w);
          setTrending(t);
          setFeed(f);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load insights');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProfileId]);

  const { intensityByIso, maxIntensity } = useMemo(() => {
    const m = new Map<string, number>();
    let max = 0;
    (world?.countries ?? []).forEach((c) => {
      const iso = String(c.iso2 || '')
        .trim()
        .toUpperCase();
      if (!iso) return;
      const t = typeof c.total === 'number' ? c.total : c.articleCount + c.clusterCount;
      m.set(iso, t);
      max = Math.max(max, t);
    });
    return { intensityByIso: m, maxIntensity: max };
  }, [world]);

  const sortedFeed = useMemo(() => (feed ? sortDevelopingFeed(feed) : []), [feed]);

  const displayKeywords = useMemo(
    () => (trending?.keywords?.length ? sortKeywordsByScoreDesc(trending.keywords) : []),
    [trending]
  );

  const displayTopics = useMemo(
    () => (trending?.topics?.length ? sortTopicsByMentionsDesc(trending.topics) : []),
    [trending]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#60a5fa' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const rootHeight = embedded
    ? '100%'
    : { xs: 'auto', md: 'calc(100vh - 80px)' };

  return (
    <Box
      sx={{
        height: rootHeight,
        minHeight: embedded ? 0 : { xs: 720, md: 560 },
        flex: embedded ? 1 : undefined,
        display: 'flex',
        flexDirection: 'column',
        p: embedded ? { xs: 2, md: 3, pt: { xs: 1, md: 1.5 }, pb: { xs: 1, md: 1.5 } } : { xs: 1, md: 2 },
        boxSizing: 'border-box',
        bgcolor: '#0a0a0a',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          flex: 1,
          minHeight: 0,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gridTemplateRows: {
            xs: 'minmax(240px,auto) minmax(220px,auto) minmax(360px,1fr)',
            md: 'minmax(0, 1fr) minmax(0, 1fr)',
          },
          gap: embedded ? 1.5 : 2,
          height: '100%',
        }}
      >
        {/* Top-left: map */}
        <Paper elevation={0} sx={{ ...panelSx, gridColumn: { md: '1' }, gridRow: { md: '1' }, minHeight: 0 }}>
          <Box sx={panelHeaderSx}>
            <Typography variant="subtitle1" sx={{ color: '#fafafa', fontWeight: 'bold' }}>
              Last 24h — articles &amp; clusters by country
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: embedded ? { xs: 200, md: 0 } : { xs: 260, md: 0 },
              p: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                maxHeight: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: OCEAN_FILL,
                borderRadius: 1,
                overflow: 'hidden',
                '& svg': { maxWidth: '100%', maxHeight: '100%', display: 'block' },
              }}
            >
            <ComposableMap
              projection="geoEqualEarth"
              width={640}
              height={320}
              projectionConfig={{ scale: 168, center: [0, 12] }}
              style={{
                width: '100%',
                height: 'auto',
                maxWidth: '100%',
                maxHeight: '100%',
                backgroundColor: 'transparent',
              }}
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const iso = getGeoIso2(geo);
                    const fill = iso
                      ? choroplethFill(iso, intensityByIso, maxIntensity)
                      : DEFAULT_COUNTRY_FILL;
                    const v = iso ? intensityByIso.get(iso) ?? 0 : 0;
                    const name = String(geoProps(geo).NAME ?? geoProps(geo).NAME_LONG ?? iso ?? '');
                    const tip = v ? `${name} — ${v} (articles+clusters in window)` : name;
                    const hoverFill = hoverFillForIntensity(v, maxIntensity);
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fill}
                        stroke="rgba(15, 23, 42, 0.45)"
                        strokeWidth={0.4}
                        aria-label={tip}
                        style={{
                          default: { outline: 'none' },
                          hover: { outline: 'none', fill: hoverFill },
                          pressed: { outline: 'none' },
                        }}
                      >
                        <title>{tip}</title>
                      </Geography>
                    );
                  })
                }
              </Geographies>
            </ComposableMap>
            </Box>
          </Box>
        </Paper>

        {/* Bottom-left: trending */}
        <Paper elevation={0} sx={{ ...panelSx, gridColumn: { md: '1' }, gridRow: { md: '2' } }}>
          <Box sx={panelHeaderSx}>
            <Typography variant="subtitle1" sx={{ color: '#fafafa', fontWeight: 'bold' }}>
              Trending keywords &amp; topics (24h)
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 2, minHeight: 0 }}>
            <Typography variant="caption" sx={{ color: '#a1a1aa', fontWeight: 600 }}>
              Keywords
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1, mb: 2 }}>
              {displayKeywords.map((k) => (
                <Chip
                  key={k.term}
                  size="small"
                  label={`${k.term} · ${(k.score * 100).toFixed(0)}%`}
                  sx={{
                    borderRadius: 0,
                    bgcolor: '#1e293b',
                    color: '#e2e8f0',
                    border: '1px solid #334155',
                    '& .MuiChip-label': { fontSize: '0.75rem' },
                  }}
                />
              ))}
            </Box>
            <Divider sx={{ borderColor: '#27272a', my: 1 }} />
            <Typography variant="caption" sx={{ color: '#a1a1aa', fontWeight: 600 }}>
              Topics
            </Typography>
            {displayTopics.length === 0 && (
              <Typography variant="body2" sx={{ color: '#71717a', mt: 1 }}>
                No topics from this profile in the last 24 hours.
              </Typography>
            )}
            <Box component="ul" sx={{ m: 0, pl: 2, mt: 1 }}>
              {displayTopics.map((t, idx) => (
                <Typography
                  key={`${t.label}-${t.mentions}-${idx}`}
                  component="li"
                  variant="body2"
                  sx={{ color: '#d4d4d8', mb: 0.75 }}
                >
                  {formatTopicLabel(t.label)}{' '}
                  <Box component="span" sx={{ color: '#71717a' }}>
                    ({t.mentions} mentions)
                  </Box>
                </Typography>
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Right: developing feed — spans two rows on md */}
        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            gridColumn: { md: '2' },
            gridRow: { md: '1 / span 2' },
            minHeight: { xs: 400, md: 0 },
          }}
        >
          <Box sx={panelHeaderSx}>
            <Typography variant="subtitle1" sx={{ color: '#fafafa', fontWeight: 'bold' }}>
              Developing clusters &amp; news
            </Typography>
            <Typography variant="caption" sx={{ color: '#a1a1aa', display: 'block', mt: 0.5 }}>
              This profile · stories with articles in the last 24 hours, most active first.
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, minHeight: 0 }}>
            {sortedFeed.length === 0 && (
              <Typography variant="body2" sx={{ color: '#a1a1aa', p: 1 }}>
                No stories for this analytic profile have articles in the last 24 hours.
              </Typography>
            )}
            {sortedFeed.map((item) => (
              <Box
                key={item.id}
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  border: '1px solid #27272a',
                  borderRadius: 1,
                  bgcolor: '#18181b',
                  '&:last-of-type': { mb: 0 },
                }}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Chip
                    size="small"
                    label={item.kind === 'cluster' ? 'Cluster' : 'Article'}
                    sx={{
                      height: 22,
                      borderRadius: 0,
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      bgcolor: item.kind === 'cluster' ? '#1e3a8a' : '#422006',
                      color: item.kind === 'cluster' ? '#bfdbfe' : '#fdba74',
                      border: `1px solid ${item.kind === 'cluster' ? '#60a5fa' : '#ea580c'}`,
                    }}
                  />
                  {item.countryIso2 && (
                    <Chip
                      size="small"
                      label={item.countryIso2}
                      sx={{ height: 22, borderRadius: 0, bgcolor: '#27272a', color: '#fafafa' }}
                    />
                  )}
                  <Typography variant="caption" sx={{ color: '#71717a', ml: 'auto' }}>
                    {(() => {
                      try {
                        return format(parseISO(item.publishedAt), 'dd/MM/yyyy HH:mm');
                      } catch {
                        return item.publishedAt;
                      }
                    })()}
                  </Typography>
                </Box>
                <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 700, mb: 0.5 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1aa', lineHeight: 1.45 }}>
                  {item.summary}
                </Typography>
                <Typography variant="caption" sx={{ color: '#52525b', mt: 0.75, display: 'block' }}>
                  {item.articleCount} article{item.articleCount === 1 ? '' : 's'} in window
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default LayoutWorldInsights;
